import { InboxAdminApi, makeRawInboxAdminApiRequest } from "@/services/Api"

import { ErrorType } from "@/hooks/useValidation"

import ErrorHandlerService from "@/services/ErrorHandler"

export type OperationProgressChunk = {
	operationType: "migration" | "rollback"
	status: "on-progress" | "completed"
	type: "instance" | "channel" | "contact"
	count: number
}

export type PreconditionType =
	| "whatsapp-channel-with-no-session"
	| "empty-message-queue"
	| "no-pending-message-blast-without-template-message"
	| "no-scheduled-snooze-without-template-message"
	| "no-active-bot-without-template-block"
	| "no-active-integration"
	| "no-existing-integration"

export type PreconditionItem = {
	type: PreconditionType
	status: "pending" | "resolved"
	severity: "warning" | "error"
	pendingResourceUrls: string[]
}

export type LastMigrationResult = {
	operationType: "migration" | "rollback"
	status: "processing" | "success" | "failed"
	stillPendingPreconditions: PreconditionItem[]
	errorCode?: MappedChannelMigrationErrorCode | string
}

export type MappedChannelMigrationErrorCode = "InboxChannelIsNotWhatsappType"

export type RunOperationResult = {
	success: boolean
	migrationResult: LastMigrationResult | null
}

class ChannelMigrationService {
	async fetchLastResult (instanceId: number): Promise<LastMigrationResult | null> {
		try {
			const { data } = await InboxAdminApi.get<LastMigrationResult>(`/instance/${instanceId}/channel-migration/last-result`)
			return data
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
			return null
		}
	}

	async runOperation (
		instanceId: number,
		operation: "migration" | "rollback",
		onOperationProgressChunk: (chunk: OperationProgressChunk) => Promise<void>
	): Promise<RunOperationResult> {
		const response = await makeRawInboxAdminApiRequest(`/instance/${instanceId}/channel-migration/operation/${operation}`, {
			method: "POST",
			body: {}
		})

		if (!response.body) {
			return {
				success: false,
				migrationResult: null
			}
		}

		const reader = response.body.getReader()
		const decoder = new TextDecoder()
		let fullResponseInString = ""

		/**
		 * While this operation runs on the Inbox API, processed results are returned
		 * incrementally in the request. This allows us to provide real-time feedback
		 * to the user. Therefore, the response is handled as a stream.
		 */
		while (true) {
			const { done, value } = await reader.read()

			if (done) {
				break
			}

			const currentPartOfResponseInString = decoder.decode(value, { stream: true })
			fullResponseInString += currentPartOfResponseInString

			const operationProgressChunkMatches = currentPartOfResponseInString.match(/\{[^{}]+\}/g) || []

			for (const operationProgressChunkInString of operationProgressChunkMatches) {
				try {
					const operationProgressChunk = JSON.parse(operationProgressChunkInString) as OperationProgressChunk

					if (operationProgressChunk.type && operationProgressChunk.status) {
						await onOperationProgressChunk(operationProgressChunk)
					}
				} catch (error) {
					ErrorHandlerService.handle(error as ErrorType)
				}
			}
		}

		try {
			const result = JSON.parse(fullResponseInString) as RunOperationResult

			return result
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)

			return {
				success: false,
				migrationResult: null
			}
		}
	}
}

export default new ChannelMigrationService()
